"use client";

import clsx from "clsx";
import {
  useStrategiesStore,
  MarketView,
  RiskLevel,
  AssetClass,
  SortField,
} from "@/stores/strategiesStore";

const marketViewOptions: { value: MarketView | "all"; label: string }[] = [
  { value: "all", label: "All Views" },
  { value: "long", label: "Long" },
  { value: "short", label: "Short" },
  { value: "neutral", label: "Neutral" },
];

const riskLevelOptions: { value: RiskLevel | "all"; label: string }[] = [
  { value: "all", label: "All Risks" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const assetClassOptions: { value: AssetClass | "all"; label: string }[] = [
  { value: "all", label: "All Assets" },
  { value: "crypto", label: "Crypto" },
  { value: "futures", label: "Futures" },
  { value: "equities", label: "Equities" },
  { value: "forex", label: "Forex" },
];

const sortOptions: { value: SortField; label: string }[] = [
  { value: "totalAum", label: "AUM" },
  { value: "lifetimeReturn", label: "Lifetime Return" },
  { value: "yearReturn", label: "1 Year Return" },
  { value: "name", label: "Name" },
];

const selectClass =
  "rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none";

export const StrategiesFilterBar = () => {
  const {
    filters,
    sortField,
    sortOrder,
    viewMode,
    setFilters,
    setSortField,
    setSortOrder,
    setViewMode,
  } = useStrategiesStore();

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      {/* Market View Filter */}
      <select
        value={filters.marketView}
        onChange={(e) => setFilters({ marketView: e.target.value as MarketView | "all" })}
        className={selectClass}
      >
        {marketViewOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Risk Level Filter */}
      <select
        value={filters.riskLevel}
        onChange={(e) => setFilters({ riskLevel: e.target.value as RiskLevel | "all" })}
        className={selectClass}
      >
        {riskLevelOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Asset Class Filter */}
      <select
        value={filters.assetClass}
        onChange={(e) => setFilters({ assetClass: e.target.value as AssetClass | "all" })}
        className={selectClass}
      >
        {assetClassOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Sort + View Toggle */}
      <div className="ml-auto flex items-center gap-2">
        {/* View Toggle */}
        <div className="flex rounded-lg border border-slate-600 bg-slate-800">
          <ViewToggleButton
            active={viewMode === "card"}
            onClick={() => setViewMode("card")}
            label="Card view"
          >
            {/* Grid icon */}
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
          </ViewToggleButton>
          <ViewToggleButton
            active={viewMode === "list"}
            onClick={() => setViewMode("list")}
            label="List view"
          >
            {/* List icon */}
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </ViewToggleButton>
        </div>

        <span className="text-sm text-slate-500">Sort by:</span>
        <select
          value={sortField}
          onChange={(e) => setSortField(e.target.value as SortField)}
          className={selectClass}
        >
          {sortOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          className="rounded-lg border border-slate-600 bg-slate-800 p-2 text-sm text-white transition hover:bg-slate-700"
        >
          {sortOrder === "asc" ? "\u2191" : "\u2193"}
        </button>
      </div>
    </div>
  );
};

const ViewToggleButton = ({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    aria-label={label}
    className={clsx(
      "px-2.5 py-2 transition",
      active ? "bg-slate-600 text-white" : "text-slate-400 hover:text-slate-200"
    )}
  >
    {children}
  </button>
);
